import React, { useState } from "react";
import { ButtonProps, CommandBarButton, depascalize } from "sprint-common-web";

import { FileRecordStatus } from "../../../../../../../common/types/FileRecordStatus";
import { FileRecordStatusDetails } from "../../../../../common/types/FileRecordStatusDetails";
import {
    canFileRecordBeChangedToRejected,
    canFileRecordBeChangedToDestroyed,
    canFileRecordBeChangedToEditing,
    canFileRecordBeChangedToQc,
    canFileRecordBeChangedToWaitingForClientResponse
} from "../../../../../common/utils/validateFileRecordStatus";
import { useFileRecordDetailsState } from "../../../../hooks/useFileRecordDetailsState";
import { FileRecordStatusDialogManager } from "./FileRecordStatusDialogManager";

export type FileRecordStatusButtonProps = Pick<ButtonProps, "disabled">;

interface FileRecordStatusButtonConfig {
    status: FileRecordStatus;
    title: "Destroyed" | "Rejected" | "Editing" | "Qc" | "Qc Done" | "Waiting for client response";
    icon: string;
    disabled?: boolean;
    tooltipMessage?: string;
}

export const FileRecordStatusButton = (props: FileRecordStatusButtonProps): JSX.Element => {
    const { fileRecordDetails, setStatus, isArchivedContentAssigned } = useFileRecordDetailsState();

    const [pickedStatus, setPickedStatus] = useState<FileRecordStatus>();

    const statusButtons: FileRecordStatusButtonConfig[] = [
        {
            status: FileRecordStatus.Editing,
            title: "Editing",
            icon: "EditNote",
            disabled: !canFileRecordBeChangedToEditing(fileRecordDetails.status)
        },
        {
            status: FileRecordStatus.Qc,
            title: "Qc",
            icon: "ComplianceAudit",
            disabled: !canFileRecordBeChangedToQc(fileRecordDetails.status)
        },
        {
            status: FileRecordStatus.QcDone,
            title: "Qc Done",
            icon: "ActivateOrders",
            disabled: isChangeToQcDoneDisabled(fileRecordDetails.status) || isArchivedContentAssigned,
            tooltipMessage: isArchivedContentAssigned
                ? "Cannot change to Qc Done while inactive content is assigned"
                : undefined
        },
        {
            status: FileRecordStatus.Rejected,
            title: "Rejected",
            icon: "PageRemove",
            disabled: !canFileRecordBeChangedToRejected(fileRecordDetails.status)
        },
        {
            status: FileRecordStatus.Destroyed,
            title: "Destroyed",
            icon: "Delete",
            disabled: !canFileRecordBeChangedToDestroyed(fileRecordDetails.status)
        },
        {
            status: FileRecordStatus.WaitingForClientResponse,
            title: "Waiting for client response",
            icon: "HourGlass",
            disabled: !canFileRecordBeChangedToWaitingForClientResponse(fileRecordDetails.status)
        }
    ];

    const getStatusButtonDetails = (status: FileRecordStatus) => {
        return statusButtons.find((details: FileRecordStatusButtonConfig) => details.status === status);
    };

    const getCurrentIcon = () => {
        return getStatusButtonDetails(fileRecordDetails.status)?.icon;
    };

    const onConfirm = (statusDetails?: FileRecordStatusDetails) => {
        if (!pickedStatus) return;

        setStatus(pickedStatus, statusDetails);
        setPickedStatus(undefined);
    };

    const isOpen = !!pickedStatus;

    return (
        <>
            <CommandBarButton
                text={`Status: ${depascalize(FileRecordStatus[fileRecordDetails.status]) || "..."}`}
                icon={getCurrentIcon()}
                disabled={props?.disabled}
                menuItems={statusButtons.map((button: FileRecordStatusButtonConfig) => ({
                    text: button.title,
                    icon: button.icon,
                    disabled: button.disabled ?? button.status === fileRecordDetails.status,
                    tooltipMessage: button.tooltipMessage,
                    onClick: () => setPickedStatus(button.status)
                }))}
                testId="file-record-status-button"
            />
            <FileRecordStatusDialogManager
                status={pickedStatus}
                isOpen={isOpen}
                onDismiss={() => setPickedStatus(undefined)}
                onConfirm={onConfirm}
            />
        </>
    );
};

const isChangeToQcDoneDisabled = (status: FileRecordStatus): boolean =>
    status === FileRecordStatus.Destroyed || status === FileRecordStatus.Rejected || status === FileRecordStatus.QcDone;
